import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { KeyService } from '../services/key.service';

/**
 * For encryption and decryption of data
 * @param req
 * @param next
 * @returns encrypted data
 */
export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
    const needToEncrypt = shouldEncrypt(req);
    const keyService = inject(KeyService);
    const keycloakService = inject(KeycloakService);

    return new Observable((observer) => {
        keyService.getClientKey().then(async () => {
            try {
                const { publicKey, kid } = await keyService.getServerKey(req.url);
                let encryptedBody: any;
                if (needToEncrypt) {
                    encryptedBody = await keycloakService.encrypt({ publicKey }, req.body, false);
                } else {
                    encryptedBody = req.body;
                }

                const setHeaders: Record<string, string | string[]> = {
                    'x-encrypted': `${needToEncrypt}`
                };
                if (!req.url.includes('signer')) {
                    setHeaders['DPop'] = await keycloakService.dpopProof(
                        req.url.startsWith('/') ? `${environment.baseUrl}${req.url}` : req.url,
                        req.method,
                        encryptedBody?.iv || '',
                        kid
                    );
                }

                if (req.url.includes('admin.lokos.in/api/keycloak/v2/graph/')) {
                    const mainUrl = req.url.split('graph');
                    const mappedUrl = `https://identity.lokos.in/admin${mainUrl[1]}`;
                    setHeaders['kc-dpop'] = await keycloakService.dpopProof(
                        mappedUrl,
                        req.method,
                        encryptedBody?.iv || '',
                        kid
                    );
                }

                next(
                    req.clone({
                        body: needToEncrypt ? { data: encryptedBody.encrypted } : req.body,
                        setHeaders
                    })
                )
                    .pipe(
                        switchMap(async (event) => {
                            if (event instanceof HttpResponse && event.headers.get('x-encrypted') === 'true') {
                                const body = event.body as { data?: string };
                                if (body && body['data']) {
                                    const decodedBody = await keycloakService.decrypt(
                                        { publicKey },
                                        body['data'],
                                        event.headers.get('x-crypto-enabled'),
                                        false
                                    );
                                    observer.next(event.clone({ body: decodedBody }));
                                } else {
                                    observer.next(event.clone({ body: {} }));
                                }
                            } else {
                                observer.next(event);
                            }
                        }),
                        catchError((error) => {
                            observer.error(error);
                            return [];
                        })
                    )
                    .subscribe({
                        complete: () => observer.complete()
                    });
            } catch (error) {
                observer.error(error);
            }
        });
    });
};

const excludedRoutes = [
    '/agey-',
    '/api/pmfme/',
    '/api/reports/',
    '/eeb/',
    '/eep/',
    '/ifc-activity/',
    '/ifc-profile',
    '/lsc/',
    '/pe-',
    '/pg-',
    '/incubator-program',
    '/ince-evaluation',
    '/incubator-challenge',
    '/role-mappings',
    '/search/',
    '/secrets',
    '/signer',
    '/vault/',
    'apis.lokos.in',
    'cdn.lokos.in',
    'userinfo',
    environment.fdmTransactionsUrl
];

const shouldEncrypt = (req: HttpRequest<unknown>) =>
    environment.enableEncryption && !excludedRoutes.some((route) => req.url.includes(route));
